import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

const LanguageContext = createContext();

const translations = {
  en: {
    // Navigation
    home: "Home",
    services: "Services",
    articles: "Articles",
    contact: "Contact",
    creditEducation: "Credit Education",
    faq: "FAQ",
    adminPortal: "Partner Login",
    
    // Hero - NEW BRAND POSITIONING
    heroTitle: "Fix Your Credit. Lower Your Bills. Fund Your Business.",
    heroCompany: "All in One Place.",
    heroSubtitle: "A nationwide financial solutions firm helping individuals and businesses fix, protect, and scale their finances. No upfront guesswork—solutions tailored to your situation. We do the work. You see the results.",
    exploreServices: "Get Started",
    contactUs: "Free Consultation",
    businessConsulting: "Nationwide Financial Solutions",
    
    // Core Value Props
    tagline: "Fix your credit. Lower your bills. Fund your business. All in one place.",
    doneForYou: "Done-for-you service",
    professionalProcess: "Professional process",
    realResults: "Real financial outcomes",
    
    // Service Divisions
    personalFinancial: "Personal Financial Solutions",
    businessFinancial: "Business Financial Solutions", 
    assetServices: "Asset Services",
    referralProgram: "Referral Partner Program",
    
    // Services Page
    ourServices: "Our Services",
    servicesSubtitle: "Comprehensive financial solutions to fix, protect, and scale your finances",
    
    // Credit Repair - Compelling Copy
    creditRepair: "Credit Repair",
    creditRepairDesc: "Boost your credit score 100+ points. Remove negative items, late payments, collections, and inaccuracies from your credit report. Results in 30-90 days.",
    creditRepairFull: "Take control of your financial future with our expert credit repair services. We leverage federal consumer protection laws (FCRA, FDCPA) to dispute inaccurate, unverifiable, and unfair negative items on your credit report. Our proven process has helped thousands qualify for mortgages, auto loans, and credit cards at better rates.",
    creditRepairWhyChoose: "Why Choose Our Credit Repair?",
    creditRepairBenefit1: "100+ Point Score Improvement",
    creditRepairBenefit1Desc: "Our average client sees a 100+ point increase within 90 days. We target the items hurting your score most.",
    creditRepairBenefit2: "No Upfront Fees",
    creditRepairBenefit2Desc: "Pay only for results. We don't charge until we deliver measurable improvements to your credit profile.",
    creditRepairBenefit3: "Expert Dispute Process",
    creditRepairBenefit3Desc: "We use proven legal strategies under FCRA, FDCPA, and FCBA to challenge inaccurate items effectively.",
    creditRepairBenefit4: "Dedicated Credit Specialist",
    creditRepairBenefit4Desc: "Work with a personal credit expert who knows your file inside and out. Direct access, fast responses.",
    
    // Credit Building
    creditBuilding: "Credit Building & Monitoring",
    creditBuildingDesc: "Build and monitor your credit with SmartCredit. Track your progress, get alerts on changes, and protect your financial future with real-time monitoring.",
    creditBuildingWhyChoose: "Why Build Credit With Us?",
    creditBuildingBenefit1: "Establish Credit History",
    creditBuildingBenefit2: "Real-Time Monitoring",
    creditBuildingBenefit3: "Identity Theft Protection",
    
    // Business Funding - Compelling Copy
    businessFunding: "Business Funding",
    businessFundingDesc: "Get up to $5M in MCA & Term Loans. Fast approval in 24-48 hours. Any credit situation welcome. No collateral required.",
    businessFundingFull: "Stop waiting weeks for bank approvals. Our business funding solutions get you the capital you need in as little as 24 hours. Whether you need working capital, equipment financing, or expansion funds, we work with 75+ lenders to find your best option. Revenue-based approval means your business performance matters more than your credit score.",
    businessFundingWhyChoose: "Why Choose Our Business Funding?",
    businessFundingBenefit1: "Fast 24-48 Hour Approval",
    businessFundingBenefit1Desc: "No waiting weeks for bank decisions. Get approved and funded within 24-48 hours to seize opportunities fast.",
    businessFundingBenefit2: "Any Credit Welcome",
    businessFundingBenefit2Desc: "Bad credit? No problem. We approve based on your business revenue, not just your personal credit score.",
    businessFundingBenefit3: "Up to $5 Million Available",
    businessFundingBenefit3Desc: "From $10K to $5M, we match you with the right funding amount for your business needs and cash flow.",
    businessFundingBenefit4: "75+ Lending Partners",
    businessFundingBenefit4Desc: "One application, 75+ lenders competing for your business. We find you the best rates and terms available.",
    
    // Merchant Services - Compelling Copy
    merchantServices: "Merchant Processing",
    merchantServicesDesc: "0% processing fees with our Cash Discount Program. FREE PAX A920 smart terminal. Save $1,000-$2,000+ monthly.",
    merchantServicesFull: "Stop losing 3-4% of every sale to credit card fees. Our Cash Discount Program lets you accept cards at 0% cost—customers who pay cash get the posted price, card users pay a small service fee. It's legal, transparent, and saves you thousands.",
    merchantServicesWhyChoose: "Why Choose Our Merchant Services?",
    merchantServicesBenefit1: "0% Processing Fees",
    merchantServicesBenefit1Desc: "Keep 100% of your sales. Our Cash Discount Program eliminates credit card processing costs completely.",
    merchantServicesBenefit2: "FREE Smart Terminal",
    merchantServicesBenefit2Desc: "Get a free PAX A920 Pro terminal ($599 value). Accept tap, chip, swipe, and mobile payments.",
    merchantServicesBenefit3: "Save $1,000-$2,000/Month",
    merchantServicesBenefit3Desc: "Most businesses save $12,000-$24,000 annually. That's real money back in your pocket.",
    merchantServicesBenefit4: "Same-Day Setup",
    merchantServicesBenefit4Desc: "Start accepting payments immediately. No long-term contracts, no hidden fees, no surprises.",
    
    // Business Credit
    businessCredit: "Business Credit Building",
    businessCreditDesc: "Build business credit separate from personal. Access better funding terms. Establish your business credit profile in 90 days.",
    businessCreditWhyChoose: "Why Build Business Credit?",
    businessCreditBenefit1: "Separate from Personal Credit",
    businessCreditBenefit2: "Higher Credit Limits",
    businessCreditBenefit3: "Better Loan Terms",
    businessCreditBenefit4: "Protect Personal Assets",
    viewDetails: "View Details",
    applyNow: "Apply Now",
    getQuote: "Get a Quote",
    getStartedToday: "Get Started Today",
    
    // Service Features
    features: "Features",
    benefits: "Benefits",
    howItWorks: "How It Works",
    whyChooseUs: "Why Choose Us",
    freeConsultation: "Free Consultation",
    fastApproval: "Fast Approval",
    expertSupport: "Expert Support",
    noHiddenFees: "No Hidden Fees",
    
    // Forms - Common
    firstName: "First Name",
    lastName: "Last Name",
    email: "Email Address",
    phone: "Phone Number",
    message: "Message (Optional)",
    submit: "Submit",
    submitting: "Submitting...",
    preferredLanguage: "Preferred Language",
    selectLanguage: "Select your preferred language",
    required: "Required",
    optional: "Optional",
    
    // Lead Form
    getYourFreeQuote: "Get Your Free Quote",
    fillOutForm: "Fill out the form below and we'll get back to you within 24 hours",
    yourInformation: "Your Information",
    
    // Auto Insurance Form
    currentlyInsured: "Are you currently insured?",
    yes: "Yes",
    no: "No",
    currentInsurer: "Current Insurance Company",
    monthlyRate: "What's your monthly rate?",
    vehicleInfo: "Vehicle Information",
    addVehicle: "Add Another Vehicle",
    removeVehicle: "Remove",
    vin: "VIN Number",
    vinOptional: "VIN (Optional)",
    ownershipType: "Ownership Type",
    owned: "Owned",
    financed: "Financed",
    leased: "Leased",
    
    // Business Funding Form
    personalInfo: "Personal Information",
    businessInfo: "Business Information",
    fundingDetails: "Funding Details",
    address: "Address",
    city: "City",
    state: "State",
    zipCode: "ZIP Code",
    dateOfBirth: "Date of Birth",
    ssn: "Social Security Number",
    ssnSecure: "Your SSN is encrypted and secure",
    ownershipPercentage: "Ownership Percentage",
    businessName: "Business Name",
    businessPhone: "Business Phone",
    ein: "EIN (Tax ID)",
    businessStartDate: "Business Start Date",
    industry: "Industry",
    entityType: "Entity Type",
    monthlyRevenue: "Monthly Revenue",
    amountRequested: "Amount Requested",
    fundingPurpose: "Purpose of Funding",
    selectIndustry: "Select Industry",
    selectEntityType: "Select Entity Type",
    selectPurpose: "Select Purpose",
    
    // Entity Types
    soleProprietor: "Sole Proprietor",
    llc: "LLC",
    corporation: "Corporation",
    partnership: "Partnership",
    
    // Funding Purposes
    workingCapital: "Working Capital",
    equipment: "Equipment Purchase",
    expansion: "Business Expansion",
    inventory: "Inventory",
    marketing: "Marketing",
    other: "Other",
    
    // Disclaimers
    serviceDisclaimer: "Service Fee Disclaimer",
    legalDisclaimer: "Legal Disclaimer",
    iAgree: "I agree to the",
    termsAndConditions: "Terms and Conditions",
    
    // Contact Page
    getInTouch: "Get in Touch",
    contactSubtitle: "Have questions? We're here to help. Reach out to our team.",
    sendMessage: "Send us a Message",
    ourOffice: "Our Office",
    callUs: "Call Us",
    emailUs: "Email Us",
    businessHours: "Business Hours",
    mondayFriday: "Monday - Friday",
    saturday: "Saturday",
    sunday: "Sunday",
    closed: "Closed",
    
    // Success/Error Messages
    thankYou: "Thank You!",
    weWillContact: "We've received your information and will contact you within 24 hours.",
    errorOccurred: "An error occurred. Please try again.",
    formSubmitted: "Form submitted successfully!",
    
    // Service Detail Pages
    backToServices: "Back to Services",
    whyChoose: "Why Choose Our",
    serviceQuestion: "Service?",
    howWeHelp: "How We Help",
    getStartedWith: "Get Started with",
    fillFormConsultation: "Fill out this form for a free consultation",
    
    // Credit Repair Page
    creditRepairTitle: "Credit Repair",
    creditRepairDesc: "Improve your credit score",
    whyChooseCreditRepair: "Why Choose Our Credit Repair Service?",
    creditBenefit1: "Leverage consumer protection laws to dispute inaccurate items",
    creditBenefit2: "Professional credit analysis and personalized strategy",
    creditBenefit3: "Challenge negative reports using FCRA, FDCPA & FCBA regulations",
    creditBenefit4: "Dedicated support with proven results - many clients see 50-150+ point improvements",
    howWeHelpCredit: "How We Help Repair Your Credit",
    getCreditReport: "Get Your Credit Report Today",
    creditReportDesc: "Would you like to view a more accurate report for just $1 today? Retrieve your report and see exactly where you stand.",
    retrieveReport: "Retrieve Your Report",
    
    // Business Funding Page
    businessFundingTitle: "Business Funding",
    businessFundingDesc: "Get the capital you need to grow",
    whyChooseFunding: "Why Choose Our Business Funding?",
    fundingBenefit1: "Access up to $5M in funding",
    fundingBenefit2: "Fast approval process - often within 24-48 hours",
    fundingBenefit3: "Multiple funding options available",
    fundingBenefit4: "Work with any credit score",
    
    // Auto Insurance Page
    autoInsuranceTitle: "Auto Insurance",
    autoInsuranceDesc: "Save money on your car insurance",
    whyChooseInsurance: "Why Choose Our Auto Insurance Service?",
    insuranceBenefit1: "Compare rates from multiple top insurers",
    insuranceBenefit2: "Saving up to 40% savings on your premiums",
    insuranceBenefit3: "Get personalized coverage recommendations",
    insuranceBenefit4: "Quick quotes in minutes",
    
    // Merchant Services Page
    merchantServicesTitle: "Merchant Services",
    merchantServicesDesc: "Accept payments with the lowest fees",
    whyChooseMerchant: "Why Choose Our Merchant Services?",
    merchantBenefit1: "Lowest processing fees in the industry",
    merchantBenefit2: "Free smart terminal with signup",
    merchantBenefit3: "Accept all payment types",
    merchantBenefit4: "Next-day funding available",
    
    // Footer
    quickLinks: "Quick Links",
    allRightsReserved: "All rights reserved",
    privacyPolicy: "Privacy Policy",
    termsOfService: "Terms of Service",
    followUs: "Follow Us",
    newsletter: "Newsletter",
    subscribeNewsletter: "Subscribe to our newsletter",
    enterEmail: "Enter your email",
    subscribe: "Subscribe",
    
    // Common
    loading: "Loading...",
    success: "Success!",
    error: "Error",
    back: "Back",
    next: "Next",
    previous: "Previous",
    close: "Close",
    readMore: "Read More",
    learnMore: "Learn More",
    seeAll: "See All",
    
    // CTA
    readyToStart: "Ready to Get Started?",
    ctaSubtitle: "Take the first step towards your financial goals today.",
    scheduleCall: "Schedule a Call",
    getFreeConsultation: "Get Free Consultation",
    readyToTakeNext: "Ready to Take Your Business to the Next Level?",
    ctaHomeSubtitle: "Let us connect you with the right solutions for your business needs. Get started today with a free consultation.",
    
    // Stats
    clientsServed: "Clients Served",
    successRate: "Success Rate",
    yearsExperience: "Years Experience",
    support: "Support",
    
    // About
    aboutUs: "About Us",
    whyChooseToptier: "Why Choose Toptier Solutions?",
    aboutDescription: "We're a nationwide financial solutions firm that does the work so you see the results. No upfront guesswork—just solutions tailored to your situation. Whether you need to repair your credit, lower your bills, or fund your business, we handle it all professionally under one roof.",
    deRiskedSolutions: "Done-For-You Service",
    deRiskedDesc: "We handle the entire process from start to finish. You focus on your life and business.",
    expertConsultants: "Professional Process",
    expertConsultantsDesc: "Our systematic approach delivers consistent, measurable results every time.",
    topTierPartners: "Real Financial Outcomes",
    topTierPartnersDesc: "Clients see real improvements: better credit scores, lower bills, funded businesses.",
    
    // Services section on homepage
    comprehensiveSolutions: "Comprehensive Financial Solutions",
    partnerDescription: "Four core divisions working together to fix, protect, and scale your finances.",
    
    // How It Works
    howItWorksStep1Form: "Fill out our quick form with your information",
    howItWorksStep1Link: "Click the link to visit our partner",
    howItWorksStep2: "Our team reviews your needs and connects you with the best options",
    howItWorksStep3: "Get personalized recommendations tailored to your situation",
    
    // Languages
    english: "English",
    spanish: "Spanish",
  },
  
  es: {
    // Navigation
    home: "Inicio",
    services: "Servicios",
    articles: "Artículos",
    contact: "Contacto",
    creditEducation: "Educación Crediticia",
    faq: "Preguntas Frecuentes",
    adminPortal: "Portal Admin",
    
    // Hero - NEW BRAND POSITIONING
    heroTitle: "Repara Tu Crédito. Reduce Tus Gastos. Financia Tu Negocio.",
    heroCompany: "Todo en Un Solo Lugar.",
    heroSubtitle: "Una firma nacional de soluciones financieras ayudando a individuos y empresas a reparar, proteger y escalar sus finanzas. Sin adivinanzas—soluciones adaptadas a tu situación. Nosotros hacemos el trabajo. Tú ves los resultados.",
    exploreServices: "Comenzar",
    contactUs: "Consulta Gratis",
    businessConsulting: "Soluciones Financieras Nacionales",
    
    // Core Value Props
    tagline: "Repara tu crédito. Reduce tus gastos. Financia tu negocio. Todo en un solo lugar.",
    doneForYou: "Servicio completo",
    professionalProcess: "Proceso profesional",
    realResults: "Resultados financieros reales",
    
    // Service Divisions
    personalFinancial: "Soluciones Financieras Personales",
    businessFinancial: "Soluciones Financieras Empresariales",
    assetServices: "Servicios de Activos",
    referralProgram: "Programa de Referidos",
    
    // Services Page
    ourServices: "Nuestros Servicios",
    servicesSubtitle: "Soluciones financieras integrales para reparar, proteger y escalar tus finanzas",
    
    // Credit Repair - Spanish
    creditRepair: "Reparación de Crédito",
    creditRepairDesc: "Aumenta tu puntaje 100+ puntos. Elimina elementos negativos, pagos atrasados, colecciones e inexactitudes de tu reporte de crédito. Resultados en 30-90 días.",
    creditRepairFull: "Toma control de tu futuro financiero con nuestros servicios expertos. Utilizamos leyes federales de protección al consumidor (FCRA, FDCPA) para disputar elementos negativos inexactos, no verificables e injustos.",
    creditRepairWhyChoose: "¿Por Qué Elegir Nuestra Reparación de Crédito?",
    creditRepairBenefit1: "Mejora de 100+ Puntos",
    creditRepairBenefit1Desc: "Nuestro cliente promedio ve un aumento de 100+ puntos en 90 días.",
    creditRepairBenefit2: "Sin Pagos Por Adelantado",
    creditRepairBenefit2Desc: "Paga solo por resultados. No cobramos hasta entregar mejoras medibles.",
    creditRepairBenefit3: "Proceso de Disputa Experto",
    creditRepairBenefit3Desc: "Usamos estrategias legales comprobadas bajo FCRA, FDCPA y FCBA.",
    creditRepairBenefit4: "Especialista Dedicado",
    creditRepairBenefit4Desc: "Trabaja con un experto personal que conoce tu archivo a fondo.",
    
    // Credit Building - Spanish
    creditBuilding: "Construcción y Monitoreo de Crédito",
    creditBuildingDesc: "Construye y monitorea tu crédito con SmartCredit. Rastrea tu progreso, recibe alertas sobre cambios y protege tu futuro financiero.",
    
    // Business Funding - Spanish
    businessFunding: "Financiamiento Empresarial",
    businessFundingDesc: "Obtén hasta $5M en MCA y Préstamos. Aprobación rápida en 24-48 horas. Cualquier situación crediticia. Sin colateral.",
    businessFundingFull: "Deja de esperar semanas por aprobaciones bancarias. Nuestras soluciones te dan el capital que necesitas en tan solo 24 horas. Trabajamos con 75+ prestamistas para encontrar tu mejor opción.",
    businessFundingWhyChoose: "¿Por Qué Elegir Nuestro Financiamiento?",
    businessFundingBenefit1: "Aprobación Rápida 24-48 Horas",
    businessFundingBenefit1Desc: "Sin esperar semanas. Aprobación y fondos en 24-48 horas.",
    businessFundingBenefit2: "Cualquier Crédito Bienvenido",
    businessFundingBenefit2Desc: "¿Mal crédito? No hay problema. Aprobamos basado en ingresos del negocio.",
    businessFundingBenefit3: "Hasta $5 Millones Disponibles",
    businessFundingBenefit3Desc: "Desde $10K hasta $5M, encontramos el monto correcto para tu negocio.",
    businessFundingBenefit4: "75+ Socios Prestamistas",
    businessFundingBenefit4Desc: "Una aplicación, 75+ prestamistas compitiendo por tu negocio.",
    
    // Auto Insurance - Spanish
    autoInsurance: "Reducción de Seguro de Auto",
    autoInsuranceDesc: "Ahorra hasta 40% en tu seguro. Compara cotizaciones de Progressive, GEICO, State Farm y más. SR-22 disponible.",
    autoInsuranceFull: "Deja de pagar de más por tu seguro de auto. Comparamos tarifas de 20+ aseguradoras para encontrarte el precio más bajo.",
    autoInsuranceWhyChoose: "¿Por Qué Elegir Nuestro Servicio de Seguros?",
    autoInsuranceBenefit1: "Ahorra Hasta 40%",
    autoInsuranceBenefit1Desc: "Nuestros clientes ahorran un promedio de $600/año.",
    autoInsuranceBenefit2: "Compara 20+ Aseguradoras",
    autoInsuranceBenefit2Desc: "Una solicitud, 20+ compañías compitiendo por tu negocio.",
    autoInsuranceBenefit3: "Cualquier Historial de Manejo",
    autoInsuranceBenefit3Desc: "¿DUI? ¿Accidentes? ¿Necesitas SR-22? Encontramos cobertura para conductores de alto riesgo.",
    autoInsuranceBenefit4: "Cotización Gratis en 5 Minutos",
    autoInsuranceBenefit4Desc: "Formulario rápido, cotizaciones instantáneas. Sin obligación.",
    
    // Merchant Services - Spanish
    merchantServices: "Procesamiento de Pagos",
    merchantServicesDesc: "0% en tarifas de procesamiento con nuestro Programa de Descuento por Efectivo. Terminal PAX A920 GRATIS. Ahorra $1,000-$2,000+ mensualmente.",
    merchantServicesFull: "Deja de perder 3-4% de cada venta en tarifas de tarjeta. Nuestro programa te permite aceptar tarjetas a 0% de costo.",
    merchantServicesWhyChoose: "¿Por Qué Elegir Nuestros Servicios para Comerciantes?",
    merchantServicesBenefit1: "0% Tarifas de Procesamiento",
    merchantServicesBenefit1Desc: "Conserva el 100% de tus ventas. Nuestro programa elimina costos de procesamiento.",
    merchantServicesBenefit2: "Terminal Inteligente GRATIS",
    merchantServicesBenefit2Desc: "Recibe una terminal PAX A920 Pro gratis (valor $599).",
    merchantServicesBenefit3: "Ahorra $1,000-$2,000/Mes",
    merchantServicesBenefit3Desc: "La mayoría de negocios ahorra $12,000-$24,000 anualmente.",
    merchantServicesBenefit4: "Configuración el Mismo Día",
    merchantServicesBenefit4Desc: "Empieza a aceptar pagos inmediatamente. Sin contratos a largo plazo.",
    
    // Tax Services - Spanish
    taxServices: "Servicios de Impuestos",
    taxServicesDesc: "Preparación profesional de impuestos para individuos y empresas. Maximiza tu reembolso. Representación ante el IRS disponible.",
    taxServicesFull: "No dejes dinero sobre la mesa. Nuestros profesionales encuentran cada deducción y crédito al que tienes derecho.",
    
    // Auto Broker - Spanish
    autoBroker: "Corretaje de Autos",
    autoBrokerDesc: "Evita los juegos del concesionario. Compramos en 100+ dealers con UNA sola verificación de crédito. Financiamiento para mal crédito disponible.",
    autoBrokerFull: "¿Odias negociar con vendedores de autos? Lo hacemos por ti. Aprovechamos relaciones con dealers y precios por volumen para ahorrarte miles.",
    
    // Business Credit - Spanish
    businessCredit: "Construcción de Crédito Empresarial",
    businessCreditDesc: "Construye crédito empresarial separado del personal. Accede a mejores términos de financiamiento. Establece tu perfil crediticio empresarial en 90 días.",
    viewDetails: "Ver Detalles",
    applyNow: "Aplicar Ahora",
    getQuote: "Obtener Cotización",
    getStartedToday: "Comienza Hoy",
    
    // Service Features
    features: "Características",
    benefits: "Beneficios",
    howItWorks: "Cómo Funciona",
    whyChooseUs: "Por Qué Elegirnos",
    freeConsultation: "Consulta Gratuita",
    fastApproval: "Aprobación Rápida",
    expertSupport: "Soporte Experto",
    noHiddenFees: "Sin Cargos Ocultos",
    
    // Forms - Common
    firstName: "Nombre",
    lastName: "Apellido",
    email: "Correo Electrónico",
    phone: "Número de Teléfono",
    message: "Mensaje (Opcional)",
    submit: "Enviar",
    submitting: "Enviando...",
    preferredLanguage: "Idioma Preferido",
    selectLanguage: "Selecciona tu idioma preferido",
    required: "Requerido",
    optional: "Opcional",
    
    // Lead Form
    getYourFreeQuote: "Obtén Tu Cotización Gratis",
    fillOutForm: "Completa el formulario y te responderemos en 24 horas",
    yourInformation: "Tu Información",
    
    // Auto Insurance Form
    currentlyInsured: "¿Tienes seguro actualmente?",
    yes: "Sí",
    no: "No",
    currentInsurer: "Compañía de Seguros Actual",
    monthlyRate: "¿Cuál es tu tarifa mensual?",
    vehicleInfo: "Información del Vehículo",
    addVehicle: "Agregar Otro Vehículo",
    removeVehicle: "Eliminar",
    vin: "Número VIN",
    vinOptional: "VIN (Opcional)",
    ownershipType: "Tipo de Propiedad",
    owned: "Propio",
    financed: "Financiado",
    leased: "Arrendado",
    
    // Business Funding Form
    personalInfo: "Información Personal",
    businessInfo: "Información del Negocio",
    fundingDetails: "Detalles del Financiamiento",
    address: "Dirección",
    city: "Ciudad",
    state: "Estado",
    zipCode: "Código Postal",
    dateOfBirth: "Fecha de Nacimiento",
    ssn: "Número de Seguro Social",
    ssnSecure: "Tu SSN está encriptado y seguro",
    ownershipPercentage: "Porcentaje de Propiedad",
    businessName: "Nombre del Negocio",
    businessPhone: "Teléfono del Negocio",
    ein: "EIN (ID Fiscal)",
    businessStartDate: "Fecha de Inicio del Negocio",
    industry: "Industria",
    entityType: "Tipo de Entidad",
    monthlyRevenue: "Ingresos Mensuales",
    amountRequested: "Cantidad Solicitada",
    fundingPurpose: "Propósito del Financiamiento",
    selectIndustry: "Seleccionar Industria",
    selectEntityType: "Seleccionar Tipo de Entidad",
    selectPurpose: "Seleccionar Propósito",
    
    // Entity Types
    soleProprietor: "Propietario Único",
    llc: "LLC",
    corporation: "Corporación",
    partnership: "Sociedad",
    
    // Funding Purposes
    workingCapital: "Capital de Trabajo",
    equipment: "Compra de Equipo",
    expansion: "Expansión del Negocio",
    inventory: "Inventario",
    marketing: "Marketing",
    other: "Otro",
    
    // Disclaimers
    serviceDisclaimer: "Descargo de Responsabilidad del Servicio",
    legalDisclaimer: "Descargo Legal",
    iAgree: "Acepto los",
    termsAndConditions: "Términos y Condiciones",
    
    // Contact Page
    getInTouch: "Ponte en Contacto",
    contactSubtitle: "¿Tienes preguntas? Estamos aquí para ayudar.",
    sendMessage: "Envíanos un Mensaje",
    ourOffice: "Nuestra Oficina",
    callUs: "Llámanos",
    emailUs: "Escríbenos",
    businessHours: "Horario de Atención",
    mondayFriday: "Lunes - Viernes",
    saturday: "Sábado",
    sunday: "Domingo",
    closed: "Cerrado",
    
    // Success/Error Messages
    thankYou: "¡Gracias!",
    weWillContact: "Hemos recibido tu información y te contactaremos en 24 horas.",
    errorOccurred: "Ocurrió un error. Por favor intenta de nuevo.",
    formSubmitted: "¡Formulario enviado con éxito!",
    
    // Service Detail Pages
    backToServices: "Volver a Servicios",
    whyChoose: "¿Por Qué Elegir Nuestro",
    serviceQuestion: "Servicio?",
    howWeHelp: "Cómo Te Ayudamos",
    getStartedWith: "Comienza con",
    fillFormConsultation: "Completa este formulario para una consulta gratis",
    
    // Credit Repair Page
    creditRepairTitle: "Reparación de Crédito",
    creditRepairDesc: "Mejora tu puntaje de crédito",
    whyChooseCreditRepair: "¿Por Qué Elegir Nuestro Servicio de Reparación de Crédito?",
    creditBenefit1: "Aprovecha las leyes de protección al consumidor para disputar items incorrectos",
    creditBenefit2: "Análisis profesional de crédito y estrategia personalizada",
    creditBenefit3: "Desafía reportes negativos usando regulaciones FCRA, FDCPA y FCBA",
    creditBenefit4: "Soporte dedicado con resultados comprobados - muchos clientes ven mejoras de 50-150+ puntos",
    howWeHelpCredit: "Cómo Te Ayudamos a Reparar Tu Crédito",
    getCreditReport: "Obtén Tu Reporte de Crédito Hoy",
    creditReportDesc: "¿Te gustaría ver un reporte más preciso por solo $1 hoy? Obtén tu reporte y ve exactamente dónde estás.",
    retrieveReport: "Obtener Mi Reporte",
    
    // Business Funding Page
    businessFundingTitle: "Financiamiento Empresarial",
    businessFundingDesc: "Obtén el capital que necesitas para crecer",
    whyChooseFunding: "¿Por Qué Elegir Nuestro Financiamiento Empresarial?",
    fundingBenefit1: "Accede hasta $5M en financiamiento",
    fundingBenefit2: "Proceso de aprobación rápido - a menudo en 24-48 horas",
    fundingBenefit3: "Múltiples opciones de financiamiento disponibles",
    fundingBenefit4: "Trabajamos con cualquier puntaje de crédito",
    
    // Auto Insurance Page
    autoInsuranceTitle: "Seguro de Auto",
    autoInsuranceDesc: "Ahorra dinero en tu seguro de auto",
    whyChooseInsurance: "¿Por Qué Elegir Nuestro Servicio de Seguro de Auto?",
    insuranceBenefit1: "Compara tarifas de múltiples aseguradoras top",
    insuranceBenefit2: "Ahorrando hasta el 40% de ahorros en tus primas",
    insuranceBenefit3: "Obtén recomendaciones de cobertura personalizadas",
    insuranceBenefit4: "Cotizaciones rápidas en minutos",
    
    // Merchant Services Page
    merchantServicesTitle: "Servicios de Merchant",
    merchantServicesDesc: "Acepta pagos con las tarifas más bajas",
    whyChooseMerchant: "¿Por Qué Elegir Nuestros Servicios de Merchant?",
    merchantBenefit1: "Las tarifas de procesamiento más bajas de la industria",
    merchantBenefit2: "Terminal inteligente gratis al registrarte",
    merchantBenefit3: "Acepta todos los tipos de pago",
    merchantBenefit4: "Fondos disponibles al día siguiente",
    
    // Footer
    quickLinks: "Enlaces Rápidos",
    allRightsReserved: "Todos los derechos reservados",
    privacyPolicy: "Política de Privacidad",
    termsOfService: "Términos de Servicio",
    followUs: "Síguenos",
    newsletter: "Boletín",
    subscribeNewsletter: "Suscríbete a nuestro boletín",
    enterEmail: "Ingresa tu correo",
    subscribe: "Suscribirse",
    
    // Common
    loading: "Cargando...",
    success: "¡Éxito!",
    error: "Error",
    back: "Atrás",
    next: "Siguiente",
    previous: "Anterior",
    close: "Cerrar",
    readMore: "Leer Más",
    learnMore: "Más Información",
    seeAll: "Ver Todo",
    
    // CTA
    readyToStart: "¿Listo para Comenzar?",
    ctaSubtitle: "Da el primer paso hacia tus metas financieras hoy.",
    scheduleCall: "Programar Llamada",
    getFreeConsultation: "Obtener Consulta Gratuita",
    readyToTakeNext: "¿Listo para Llevar Tu Negocio al Siguiente Nivel?",
    ctaHomeSubtitle: "Permítenos conectarte con las soluciones adecuadas para las necesidades de tu negocio. Comienza hoy con una consulta gratuita.",
    
    // Stats
    clientsServed: "Clientes Atendidos",
    successRate: "Tasa de Éxito",
    yearsExperience: "Años de Experiencia",
    support: "Soporte",
    
    // About
    aboutUs: "Sobre Nosotros",
    whyChooseToptier: "¿Por Qué Elegir Toptier Solutions?",
    aboutDescription: "En Toptier Solutions, entendemos que cada decisión empresarial conlleva riesgos. Por eso hemos construido una red de socios afiliados confiables en múltiples industrias. Nuestro enfoque de consultoría garantiza que obtengas orientación experta y las mejores opciones sin la carga del compromiso.",
    deRiskedSolutions: "Soluciones Sin Riesgo",
    deRiskedDesc: "Nuestro modelo de afiliados asegura que obtengas las mejores opciones sin compromiso.",
    expertConsultants: "Consultores Expertos",
    expertConsultantsDesc: "Nuestro equipo tiene décadas de experiencia combinada en consultoría empresarial.",
    topTierPartners: "Socios de Primera Clase",
    topTierPartnersDesc: "Solo trabajamos con las mejores empresas afiliadas en cada industria.",
    
    // Services section on homepage
    comprehensiveSolutions: "Soluciones Empresariales Integrales",
    partnerDescription: "Nos asociamos con empresas líderes en la industria para brindarte las mejores opciones para tus necesidades específicas.",
    
    // How It Works
    howItWorksStep1Form: "Llena nuestro formulario rápido con tu información",
    howItWorksStep1Link: "Haz clic en el enlace para visitar a nuestro socio",
    howItWorksStep2: "Nuestro equipo revisa tus necesidades y te conecta con las mejores opciones",
    howItWorksStep3: "Recibe recomendaciones personalizadas adaptadas a tu situación",
    
    // Languages
    english: "Inglés",
    spanish: "Español",
  }
};

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // ALWAYS start with English - this is the default for everyone
  const [language, setLanguageState] = useState('en');
  
  // Track if URL language has been applied (to avoid overriding user clicks)
  const [urlLanguageApplied, setUrlLanguageApplied] = useState(false);

  // On mount only, check if there's a URL param
  useEffect(() => {
    const urlLang = searchParams.get('lang');
    if (urlLang && translations[urlLang] && !urlLanguageApplied) {
      setLanguageState(urlLang);
      localStorage.setItem('preferredLanguage', urlLang);
      localStorage.setItem('languageExplicitlySet', 'true');
      setUrlLanguageApplied(true);
    } else if (!urlLanguageApplied) {
      // Check for stored preference
      const saved = localStorage.getItem('preferredLanguage');
      const wasExplicitlySet = localStorage.getItem('languageExplicitlySet');
      if (saved && translations[saved] && wasExplicitlySet === 'true') {
        setLanguageState(saved);
      }
      setUrlLanguageApplied(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount - intentionally ignore searchParams changes

  // Update document lang attribute
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const t = (key) => {
    return translations[language]?.[key] || translations['en']?.[key] || key;
  };

  const changeLanguage = (lang) => {
    if (translations[lang]) {
      console.log('[LanguageContext] Changing language to:', lang);
      setLanguageState(lang);
      localStorage.setItem('preferredLanguage', lang);
      localStorage.setItem('languageExplicitlySet', 'true');
    }
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage: changeLanguage, t, translations }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
